'use client';

import { RefCallback, useEffect, useState } from 'react';

import { getAppConfig } from '@services/config';
import { familyAdvantagesPredicate, getFamilyContract } from '@services/mcube';

import { getImpactSimulationServer } from '@actions/get-impacts';
import { getServerSession } from '@actions/get-session';

import { MarketLineType } from '@app-types/contract';
import { Advantage } from '@app-types/simulator/simulator';
import { UserType } from '@app-types/user';

import { BigIcon } from '@components/big';
import { EligibleDiscounts, LoginPrompt } from '@components/connection';
import { LineType } from '@components/connection/lines';

import { paths } from '@helpers/path';
import { formatPhoneNumber } from '@helpers/phone-number';
import { noop } from '@helpers/try-catch';

const appConfig = getAppConfig();

interface ConnectionIncentiveProps {
    ref?: RefCallback<HTMLElement>;
    className?: string;
}

export function ConnectionIncentive({ ref }: Readonly<ConnectionIncentiveProps>) {
    const [user, setUser] = useState<UserType | undefined>(undefined);
    const [proposedLines, setProposedLines] = useState<LineType[]>([]);
    const [eligibleLines, setEligibleLines] = useState<LineType[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const refreshData = async () => {
        try {
            const simulation = await getImpactSimulationServer([appConfig.defaultPlans.simulator.fai]);

            const faiOffer = simulation?.proposed?.filter(
                (prop) =>
                    prop.applicableOp?.advantages?.some((a) => familyAdvantagesPredicate(a)) &&
                    prop.applicableOp?.marketLine === MarketLineType.FAI,
            );
            let faiPromo: Advantage | undefined;
            if (faiOffer) {
                faiPromo = faiOffer[0]?.applicableOp?.advantages?.find((a) => familyAdvantagesPredicate(a));
            }

            setProposedLines([
                {
                    label: 'Bbox must ou ultym',
                    discount: faiPromo?.discountIncludingTax,
                    marketLine: MarketLineType.FAI,
                },
            ]);

            if (simulation) {
                const eligible: LineType[] = getFamilyContract(simulation).map((contract) => ({
                    label:
                        formatPhoneNumber(contract.msisdn || contract.voipNumber) ||
                        contract.applicableOp?.commercialLabel,
                    discount:
                        contract.applicableOp.advantages?.filter(familyAdvantagesPredicate)[0]?.discountIncludingTax,
                    marketLine: contract.applicableOp.marketLine as MarketLineType,
                }));

                setEligibleLines(eligible);
            }

            setIsLoading(false);
        } catch (error) {
            setHasError(true);
            console.error('Impossible de rafraichir le panier virtuel', error);
        }
    };

    useEffect(() => {
        const fetchClientData = async () => {
            return await getServerSession();
        };

        fetchClientData()
            .then((session) => {
                setUser(session?.user as UserType | undefined);
                refreshData().catch(noop);
            })
            .catch(() => {
                setUser(undefined);
            });
    }, []);

    if (!isLoading && hasError) {
        return null;
    }

    return (
        <div ref={ref}>
            {user ? (
                <EligibleDiscounts
                    user={user}
                    proposedLines={proposedLines}
                    proposedLinesLabel='Vous choisissez votre nouvelle Bbox'
                    eligibleLines={eligibleLines}
                    isLoading={isLoading}
                    noProposedLinesOptions={{
                        title: (
                            <>
                                Il ne vous manque qu’un forfait pour faire des <BigIcon /> économies&nbsp;!
                            </>
                        ),
                        description: null,
                        cta: {
                            href: paths.plans,
                            label: 'Voir les forfaits',
                        },
                    }}
                />
            ) : (
                <LoginPrompt />
            )}
        </div>
    );
}
